import Direction3D, { Direction3DInterface } from './Direction3D';
import HalfSpace3D from './HalfSpace3D';
import IsometricEmbedding from './IsometricEmbedding';
import Line3D from './Line3D';
import LineSegment3D from './LineSegment3D';
import Point3D, { Point3DInterface } from './Point3D';
import Polygon3D from './Polygon3D';
import { distanceEquals, distanceGreaterThan, isDotProductZero } from './units';
import Vector3D from './Vector3D';

interface Plane3DInterface {
  readonly basePoint: Point3DInterface;
  readonly normalDirection: Direction3DInterface;
}

class Plane3D {
  // A half-space whose boundary is this plane.
  private readonly _halfSpace: HalfSpace3D;

  private constructor(halfSpace: HalfSpace3D) {
    this._halfSpace = halfSpace;
  }

  static halfSpaceBoundary(halfSpace: HalfSpace3D) {
    return new Plane3D(halfSpace);
  }

  // Create a plane that is perpendicular to the provided direction and contains the specified point.
  static fromPerpendicularDirectionAndPoint(direction: Direction3D, point = Point3D.origin) {
    return new Plane3D(HalfSpace3D.fromDirectionAndBoundaryPoint(direction, point));
  }

  static fromObject(planeObject: Plane3DInterface) {
    return Plane3D.fromPerpendicularDirectionAndPoint(
      Direction3D.fromDirectionalVector(Point3D.fromObject(planeObject.normalDirection))!,
      Point3D.fromObject(planeObject.basePoint),
    );
  }

  // Create a plane that contains the specified point and contains a line parallel to the specified direction.
  static fromPointsAndDirectionInPlane(point0: Point3D, point1: Point3D, direction: Direction3D): Plane3D | null {
    const direction1 = Direction3D.fromPointToPoint(point0, point1);
    if (!direction1) {
      return null;
    }
    const normal = direction1.crossProductDirection(direction);
    return normal && Plane3D.fromPerpendicularDirectionAndPoint(normal, point0);
  }

  static fromPoints(point0: Point3D, point1: Point3D, point2: Point3D): Plane3D | null {
    const direction = Direction3D.fromPointToPoint(point0, point2);
    return direction && Plane3D.fromPointsAndDirectionInPlane(point0, point1, direction);
  }

  getEmbedding() {
    return IsometricEmbedding.fromPointAndNormalDirection(this.pointNearestOrigin, this._halfSpace.direction);
  }

  // Return the two half-spaces whose boundaries are this plane.
  get sides() {
    return [this._halfSpace, this._halfSpace.opposite()];
  }

  // The point contained in this plane that is closest to the origin.
  get pointNearestOrigin() {
    return this._halfSpace.direction.toPoint(this._halfSpace.offsetFromOrigin);
  }

  get halfSpace() {
    return this._halfSpace;
  }

  equals(other: Plane3D) {
    return (
      this.containsPoint(other.pointNearestOrigin) && this.halfSpace.direction.isParallelTo(other.halfSpace.direction)
    );
  }

  // When computing the intersection of three planes, if all the planes pass through the origin, then the intersection
  // is the origin (assuming the intersection is a single point). Otherwise, if a plane is displaced from the origin, we
  // need to adjust the intersection point. If we compute this adjustment for all three planes based on their
  // displacement from the origin and add these adjustments, we'll get the actual intersection of the planes.
  //
  // This method computes the adjustment for this plane based on the other intersection planes.
  private computeIntersectionAdjustment(plane1: Plane3D, plane2: Plane3D): Point3D | null {
    // Compute the direction of the line contained in plane1 and plane2.
    const directionOfIntersectedLine = plane1._halfSpace.direction.crossProductDirection(plane2._halfSpace.direction);
    if (!directionOfIntersectedLine) {
      return null;
    }
    // Compute the component of directionOfIntersectedLine perpendicular to this plane.
    const perpendicularComponent = this._halfSpace.direction.dotProduct(directionOfIntersectedLine);
    if (isDotProductZero(perpendicularComponent)) {
      return null;
    }
    return directionOfIntersectedLine.toPoint(this._halfSpace.offsetFromOrigin / perpendicularComponent);
  }

  public intersectWithPlanes(plane1: Plane3D, plane2: Plane3D): Point3D | null {
    const offset0 = this.computeIntersectionAdjustment(plane1, plane2);
    const offset1 = plane1.computeIntersectionAdjustment(plane2, this);
    const offset2 = plane2.computeIntersectionAdjustment(this, plane1);
    return offset0 && offset1 && offset2 && offset0.add(offset1).add(offset2);
  }

  public intersectWithPlane(other: Plane3D): Line3D | null {
    const direction = this._halfSpace.direction.crossProductDirection(other._halfSpace.direction);
    if (!direction) {
      return null;
    }
    const pointOnLine = this.intersectWithPlanes(other, Plane3D.fromPerpendicularDirectionAndPoint(direction));
    return pointOnLine && new Line3D(direction, pointOnLine);
  }

  public intersectWithLine(line: Line3D): Point3D | null {
    // The signed distance from line.pointNearestOrigin to this plane in the direction of this._halfSpace.direction.
    const offsetToPlane =
      -line.pointNearestOrigin.dotProduct(this._halfSpace.direction.toPoint()) + this._halfSpace.offsetFromOrigin;
    // The cosine of the angle between this._halfSpace.direction and line.direction.
    const cosineOfAngle = this._halfSpace.direction.dotProduct(line.direction);
    if (isDotProductZero(cosineOfAngle)) {
      return null;
    }
    return line.pointNearestOrigin.add(line.direction.toPoint(offsetToPlane / cosineOfAngle));
  }

  angleBetweenNormals(plane: Plane3D) {
    return this.halfSpace.direction.angleBetween(plane.halfSpace.direction);
  }

  signedDistanceTo(point: Point3D): number {
    return Vector3D.fromPointAndPoint(this.pointNearestOrigin, point).getComponentInDirection(
      this._halfSpace.direction,
    );
  }

  pointIsOnNormalSide(point: Point3D): boolean {
    return distanceGreaterThan(this.signedDistanceTo(point), 0);
  }

  isParallelTo = (plane: Plane3D): boolean => this._halfSpace.direction.isParallelTo(plane._halfSpace.direction);

  containsPoint(point: Point3D) {
    return distanceEquals(this.signedDistanceTo(point), 0);
  }

  containsSegment(segment: LineSegment3D) {
    return this.containsPoint(segment.basePoint) && this.containsPoint(segment.endPoint);
  }

  containsLine(line: Line3D): boolean {
    return (
      this.containsPoint(line.pointNearestOrigin) &&
      isDotProductZero(this._halfSpace.direction.dotProduct(line.direction))
    );
  }

  containsPolygon(polygon: Polygon3D) {
    return this.equals(polygon.asPlane());
  }

  reverse() {
    return new Plane3D(this.halfSpace.opposite());
  }

  orientNormalWith(direction: Direction3D) {
    return this.halfSpace.direction.dotProduct(direction) < 0 ? this.reverse() : this;
  }

  translate(translation: Vector3D) {
    return Plane3D.fromPerpendicularDirectionAndPoint(
      this.halfSpace.direction,
      this.pointNearestOrigin.translate(translation),
    );
  }

  toObject(): Plane3DInterface {
    return {
      basePoint: this.pointNearestOrigin.toObject(),
      normalDirection: this.halfSpace.direction.toObject(),
    };
  }
}

export default Plane3D;
